// @ts-strict-ignore
/** @jsx jsx */
import { jsx } from 'theme-ui'
import React, { Fragment } from 'react'
import { Button } from '../../atoms'
import { TruArrow, TruCheckmark } from '@uswitch/spark-icons/tru'
import FormStepField from './form-step-field'
import { isEmpty } from '../../utils/obj'
import { useComponentTracking } from '../../utils/component-events'
import { trackFields } from '../../utils/form'
import ProgressCircle from './progress-circle'

const getFields = (step = {}) => step.fields
const getFieldNames = (fields = []) => {
  return fields
    .map(({ name, children }) => (children ? getFieldNames(children) : name))
    .reduce((arr, nameOrNames) => arr.concat(nameOrNames), [])
}

const FormStepFields = ({
  fields,
  register,
  errors,
  setValue,
  trigger,
  variant,
  watch,
  getValues,
  goToPage,
  updateCompletionState,
  onKeyDownFunction,
  addressLookup,
  privacyPolicyLink,
  trackInteraction = () => {},
  sectionTitle,
  defaultValues,
  fieldTracking,
}) => (
  <For each='field' of={fields} index='i'>
    <FormStepField
      field={field}
      key={`field-${i}`}
      register={register}
      errors={errors}
      setValue={setValue}
      trigger={trigger}
      variant={variant}
      watch={watch}
      getValues={getValues}
      goToPage={goToPage}
      updateCompletionState={updateCompletionState}
      onKeyDownFunction={onKeyDownFunction}
      trackInteraction={trackInteraction}
      sectionTitle={sectionTitle}
      index={i}
      addressLookup={addressLookup}
      privacyPolicyLink={privacyPolicyLink}
      defaultValues={defaultValues}
      fieldTracking={fieldTracking}
    />
  </For>
)

const FormStep = ({
  step,
  register,
  disableButtonUntilValid,
  errors,
  setValue,
  trigger,
  activeIndex,
  stepIndex,
  variant = 'default',
  goBack,
  goForward,
  goToFirst,
  goToLast,
  totalSteps,
  goToPage,
  goToUrl,
  watch,
  isErrored,
  isLoading,
  isValid,
  updateCompletionState,
  getValues,
  editFromSummary,
  withBackButton,
  trackInteraction = () => {},
  query,
  handleSubmit,
  onSubmit,
  addressLookup,
  meta = {},
  brand,
  module,
  defaultValues,
  fieldTracking,
  schema: { integrationType },
}) => {
  const isVisible = (field) => {
    if (!field.visibleIf) return true

    return (
      (field.visibleIf.or || []).some((e) => watch(e.field) === e.equals) &&
      (field.visibleIf.and || []).every((e) => watch(e.field) === e.equals)
    )
  }
  const getStatePath = (variant = 'panel', state = 'base', path = '', submitSection = false) =>
    `elements.form-section.${variant}.${state === 'base' ? 'elements' : `states.${state}`}.${path}${
      submitSection ? '.submit' : ''
    }`

  const getVisibleFields = (fields: any[] = []) => fields.filter((field: any) => field.isVisible)

  const markFieldsAsVisible = (fields = []) =>
    fields.map((field: any) => {
      if (field.children) {
        field.children = markFieldsAsVisible(field.children)
      }
      return { ...field, isVisible: isVisible(field) }
    })

  const { formError } = step
  const { privacyPolicyLink } = meta
  const isActive = activeIndex === stepIndex
  const title = step.sectionName
  const description = step?.sectionDescription
  const fields = markFieldsAsVisible(getFields(step))
  const isComplete = activeIndex > stepIndex
  const state = isActive ? 'isActive' : isComplete ? 'isComplete' : 'base'
  const variantPath = (path, submitSection) => getStatePath(variant, state, path, submitSection)
  const buttonProps = Array.isArray(step.buttonProps)
    ? getVisibleFields(markFieldsAsVisible(step.buttonProps))
    : getVisibleFields(markFieldsAsVisible([step.buttonProps]))
  const submitSection = title === 'Submit'
  const formFieldVariantToTheme = {
    default: 'default',
    simple: 'simple',
    panel: 'panel',
    journey: 'journey',
    'mortgages-broker': 'mortgages-broker',
    slide: 'slide',
    'platinum-ppc': 'platinum-ppc',
    compact: 'compact',
  }
  const trackComponent = useComponentTracking()

  const { id } = step

  const validateButtonFunction = async (func) => {
    const passedValidation = await trigger(getFieldNames(getVisibleFields(fields)))

    if (passedValidation) {
      if (query.formCompleted) {
        return handleSubmit(onSubmit)
      }

      trackComponent(
        'Form Event',
        'stepSubmitted',
        fields.reduce(
          (o, f) => ({ [f.name]: f.trackInput ? getValues(f.name) : 'untracked', ...o }),
          {
            sectionName: step.sectionName,
            sectionIndex: stepIndex,
          },
        ),
      )

      const gtmTrackingFields = trackFields(fields)

      if (gtmTrackingFields.length) {
        const fieldValuesToTrack = gtmTrackingFields.map(
          (fieldToTrack) =>
            fieldToTrack && { fieldName: fieldToTrack, values: getValues(fieldToTrack) },
        )
        fieldValuesToTrack.forEach(({ fieldName, values }) => {
          trackInteraction('GA Form Step Submitted', 'Form Interaction', `${fieldName}:${values}`)
        })
      }
      return func()
    }
  }

  const selectButtonFunction = (action, url) => {
    const btnFuncs = {
      goForward: () => validateButtonFunction(goForward),
      goBack,
      goToFirst,
      goToLast,
      editFromSummary: () => validateButtonFunction(editFromSummary),
      redirect: () => goToUrl(url),
    }

    return btnFuncs[action]
  }

  const onKeyDownFunction = buttonProps
    .map(
      ({ action, url }) =>
        (action === 'editFromSummary' || action === 'goForward' || action === 'redirect') &&
        selectButtonFunction(action, url),
    )
    .find(Boolean)

  return (
    <Fragment>
      <div id={id} sx={{ variant: variantPath('wrapper', submitSection) }}>
        <If condition={withBackButton && activeIndex !== 0}>
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/interactive-supports-focus */}
          <div
            role='button'
            sx={{ ml: ['sm', 'xxl', 'xxxl'], mb: 'sm', cursor: 'pointer', mt: 'sm' }}
            onClick={() => goBack()}
          >
            <TruArrow color={'black'} sx={{ transform: 'rotate(0.75turn)' }} size={20} />
          </div>
        </If>
        <div sx={{ variant: variantPath('heading', submitSection) }}>
          <If condition={!submitSection && title}>
            <h2
              sx={{
                marginY: 20,
                fontWeight: 700,
                fontSize: 24,
                mr: ['md', 'xl', 'xl'],
                variant: variantPath('heading.sectionName', submitSection),
              }}
            >
              {title}
            </h2>
          </If>
          <If condition={!submitSection && description}>
            <h3
              sx={{
                mr: ['md', 'xl', 'xl'],
                variant: variantPath('heading.sectionDescription', submitSection),
              }}
            >
              {description}
            </h3>
          </If>
          <If condition={isComplete}>
            <div sx={{ display: 'flex', alignItems: 'center' }}>
              <div
                role='button'
                sx={{ mr: 'md', cursor: 'pointer', color: 'grey-60' }}
                onKeyDown={() => goToPage(stepIndex)}
                onClick={() => {
                  goToPage(stepIndex)
                }}
                tabIndex='0'
              >
                Edit
              </div>
              <TruCheckmark color='#64CD8D' size={32} />
            </div>
          </If>
        </div>
        <div sx={{ variant: variantPath('fields', submitSection) }}>
          <FormStepFields
            fields={fields}
            register={register}
            trigger={trigger}
            errors={errors}
            setValue={setValue}
            variant={formFieldVariantToTheme[variant]}
            watch={watch}
            getValues={getValues}
            goToPage={goToPage}
            updateCompletionState={updateCompletionState}
            onKeyDownFunction={onKeyDownFunction}
            trackInteraction={trackInteraction}
            sectionTitle={title}
            addressLookup={addressLookup}
            privacyPolicyLink={privacyPolicyLink}
            defaultValues={defaultValues}
            fieldTracking={fieldTracking}
          />
        </div>
        <If condition={step.buttonProps}>
          {buttonProps.map(({ label, variant, action, withIcon, url }, index) => {
            const shouldSubmit = stepIndex === totalSteps - 1 || query.formCompleted
            const onClick = selectButtonFunction(action, url)

            return (
              <div
                key={index}
                className={
                  integrationType === 'eligibility'
                    ? 'FormStep__buttonWrapper__eligibility'
                    : undefined
                }
                {...(integrationType !== 'eligibility' && {
                  sx: { variant: variantPath('buttonWrapper', submitSection) },
                })}
              >
                <Button
                  type={shouldSubmit ? 'submit' : 'button'}
                  disabled={
                    (disableButtonUntilValid && !isValid) ||
                    !isActive ||
                    !isEmpty(
                      Object.keys(errors).filter((e) => fields.map((f) => f.name).includes(e)),
                    ) ||
                    isLoading
                  }
                  onClick={onClick}
                  variant={module || variant}
                  key={`${label}-${index}`}
                  size={step.buttonProps.size || 'lg'}
                  withIcon={brand === 'confused'}
                >
                  {query.formCompleted ? 'Update results' : label}
                  {isLoading && integrationType !== 'eligibility' && <ProgressCircle height={24} />}
                  <If condition={withIcon}>
                    <TruArrow sx={{ transform: 'rotate(0.25turn)' }} color='white' size={18} />
                  </If>
                </Button>
              </div>
            )
          })}
        </If>
        <If condition={formError && isErrored}>
          <div sx={{ variant: `elements.form.variants.${variant}.input.formError` }}>
            <p sx={{ variant: `elements.form.variants.${variant}.input.formError.title` }}>
              {formError.title}
            </p>
            <p>{formError.description}</p>
          </div>
        </If>
      </div>
      <If condition={step.postSection}>
        <div sx={{ marginLeft: ['auto', '-200px'], marginBottom: 'lg', width: ['auto', ' 960px'] }}>
          {step.postSection.filter(isVisible).map((e, i) => (
            <p key={i} sx={{ fontWeight: e.bold ? 'bold' : 'normal' }}>
              {e.text}
            </p>
          ))}
        </div>
      </If>
    </Fragment>
  )
}

export default FormStep
